import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Loader2, ChevronRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { directoryApi } from "@/lib/api/directory";
import { iconForCategory } from "@/lib/categoryIcons";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

function L2Card({ node, lang }) {
    const Icon = iconForCategory(node.name_en);
    const primary = lang === "ar" ? node.name_ar : node.name_en;
    const secondary = lang === "ar" ? node.name_en : node.name_ar;
    const isPoly = (node.parent_ids || []).length > 1;
    return (
        <Link
            to={`/member/providers/${node.id}`}
            className="surface-card p-5 group hover:border-primary/40 hover:shadow-md transition-all flex flex-col gap-3"
            data-testid={`directory-l2-${node.id}`}
        >
            <div className="flex items-start justify-between">
                <div className="icon-tile h-11 w-11">
                    <Icon className="h-5 w-5" />
                </div>
                {isPoly && (
                    <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                        Cross-listed
                    </Badge>
                )}
            </div>
            <div className="min-w-0">
                <h3 className="text-base font-semibold text-foreground truncate">{primary}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground truncate" lang={lang === "ar" ? "en" : "ar"}>
                    {secondary}
                </p>
                {node.explanatory_name_ar && (
                    <p className="mt-2 text-xs text-muted-foreground line-clamp-2 font-arabic" dir="rtl">
                        {node.explanatory_name_ar}
                    </p>
                )}
            </div>
            <div className="mt-auto pt-2 flex items-center text-primary text-xs font-medium">
                <span>{lang === "ar" ? "اعرض المزودين" : "View providers"}</span>
                <ChevronRight className="h-3.5 w-3.5 ms-1 group-hover:translate-x-0.5 transition-transform" />
            </div>
        </Link>
    );
}

export default function CategoryViewPage() {
    const { t, lang } = useI18n();
    const { id } = useParams();
    const [data, setData] = useState(null);

    useEffect(() => {
        let active = true;
        directoryApi.categoryDetail(id)
            .then((d) => { if (active) setData(d); })
            .catch((err) => active && toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message));
        return () => { active = false; };
    }, [id]);

    if (!data) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]" data-testid="page-member-category-loading">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const primary = lang === "ar" ? data.name_ar : data.name_en;
    const secondary = lang === "ar" ? data.name_en : data.name_ar;

    return (
        <div className="max-w-6xl space-y-6 fade-up" data-testid="page-member-category">
            <Link
                to="/member/home"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
                data-testid="back-to-home"
            >
                <ChevronLeft className="h-4 w-4 me-1" />
                {t("common.back")}
            </Link>

            <header>
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{primary}</h1>
                <p className="mt-1 text-sm text-muted-foreground" lang={lang === "ar" ? "en" : "ar"}>
                    {secondary}
                </p>
                {data.explanatory_name_ar && (
                    <p className="mt-2 text-sm text-muted-foreground font-arabic" dir="rtl">
                        {data.explanatory_name_ar}
                    </p>
                )}
            </header>

            <section>
                <h2 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                    {t("member.category.subcategories", { n: data.children?.length || 0 })}
                </h2>
                {data.children?.length === 0 ? (
                    <div className="surface-card p-10 text-center text-sm text-muted-foreground">
                        {t("member.category.empty")}
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.children?.map((c) => (
                            <L2Card key={c.id} node={c} lang={lang} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
